import { useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import { PhotosDisabledError, PhotoUploadRefusedError, uploadEventPhoto } from '../api/eventPhotos';
import { Button } from '../ui/Button';
import { TextField } from '../ui/Field';
import { Overlay } from '../ui/Overlay';

/**
 * Adding photographs to one event — the control, the caption sheet and the upload itself.
 *
 * **Extracted by TROOP-119 because a second screen needed it.** The flow lived inside
 * `EventGallery` when the event's own page was the only way in; the photo feed adds a second, and a
 * copied file input plus a copied sequential loop is how the two would drift — one of them growing
 * a parallel upload the moment somebody noticed it was slow, and discovering the rate limit in
 * production. One component, both callers.
 *
 * **The caption is asked after the files are picked, not before.** It applies to the whole batch,
 * so a field sitting open above a grid is captioning photographs nobody has selected yet.
 *
 * **`unitId` is the door, not the event's unit.** Undefined means `/family`, which is the route a
 * guardian actually has — the feed's rows each carry a `unitId` and passing *that* would send a
 * parent down `/units/`, where `UnitScopeMiddleware` 404s them before this endpoint runs.
 */
export function PhotoUpload({
  unitId,
  eventId,
  eventTitle,
  label = 'Add photos',
  onAdded,
  onDisabled,
  onRefused,
}: {
  /** Undefined on `/family` — that absence is what selects the guardian's door. */
  unitId?: string;
  eventId: string;
  /**
   * Names the event in the sheet. Passed by the feed, where several events are on screen at once
   * and the sheet is the last chance to notice you tapped the wrong weekend. Omitted on the
   * event's own page, where there is only one thing it could mean.
   */
  eventTitle?: string;
  label?: string;
  onAdded: () => void | Promise<void>;
  /** The unit turned photos off mid-flight. The caller renders the explanation; §6. */
  onDisabled?: (message: string) => void;
  /**
   * The unit narrowed who may add, mid-flight (TROOP-225). Separate from {@link onDisabled} because
   * the two are different states — the gallery is still open here — and the caller says so
   * differently. Reaching this means the control was drawn on a stale answer, so the honest
   * response is to take the answer again rather than to leave the button sitting there.
   */
  onRefused?: (message: string) => void;
}) {
  const [picked, setPicked] = useState<File[] | null>(null);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInput = useRef<HTMLInputElement>(null);

  function clear() {
    setPicked(null);
    setCaption('');
    if (fileInput.current) fileInput.current.value = '';
  }

  async function upload() {
    if (!picked?.length) return;

    setBusy(true);
    setError(null);

    try {
      // Sequential rather than parallel, on purpose. The upload route is rate limited per person
      // (TROOP-109) and each one holds the single replica through a decode and a resize, so firing
      // forty at once is how a leader emptying their phone gets a 429 they did nothing to deserve.
      for (const file of picked) {
        await uploadEventPhoto(unitId, eventId, file, caption || undefined);
      }

      clear();
      await onAdded();
    } catch (e) {
      if (e instanceof PhotosDisabledError) {
        clear();
        onDisabled?.(e.message);
      } else if (e instanceof PhotoUploadRefusedError) {
        // Photos are still on and still readable; what changed is who fills them. The sheet closes
        // and the caller replaces the control with a sentence naming who does.
        clear();
        onRefused?.(e.message);
      } else {
        setError(e instanceof Error ? e.message : 'That photo could not be added.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* Hidden and driven by the Button, so the control carries the design system's styling
          rather than the browser's. Choosing files opens the sheet rather than uploading straight
          away, which is where the caption is asked. */}
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = e.target.files;
          if (files?.length) setPicked(Array.from(files));
        }}
      />

      <Button
        variant="secondary"
        onClick={() => fileInput.current?.click()}
        disabledReason={busy ? 'Uploading...' : undefined}
      >
        <Camera size={16} aria-hidden />
        {label}
      </Button>

      {error && (
        <p
          className="t-caption"
          role="alert"
          style={{ color: 'var(--color-status-blocked)', margin: 0 }}
        >
          {error}
        </p>
      )}

      {picked && (
        <Overlay
          title={picked.length === 1 ? 'Add this photo' : `Add these ${picked.length} photos`}
          onClose={clear}
        >
          <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
            {eventTitle && (
              <p className="t-body-strong" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
                To {eventTitle}
              </p>
            )}

            <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
              {picked.length === 1
                ? picked[0].name
                : `${picked
                    .map((f) => f.name)
                    .slice(0, 3)
                    .join(', ')}${picked.length > 3 ? ` and ${picked.length - 3} more` : ''}`}
            </p>

            {/* Just "Caption" -- FieldShell appends "(optional)" itself for any field that is not
                required, so spelling it out here renders "CAPTION (OPTIONAL) (optional)". */}
            <TextField
              label="Caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Day two, summit hike"
              maxLength={500}
              hint={
                picked.length > 1
                  ? 'Goes on all of them. Leave it blank to add them without one.'
                  : undefined
              }
            />

            <div>
              <Button onClick={() => void upload()} disabledReason={busy ? 'Uploading...' : undefined}>
                {picked.length === 1 ? 'Add photo' : `Add ${picked.length} photos`}
              </Button>
            </div>
          </div>
        </Overlay>
      )}
    </>
  );
}
